class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      evt => evt.code === 'Escape' && this.close()
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[data-cart-toggle]').forEach(elem => {
      let cartLink = elem;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', event => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', event => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.updateDrawerMetrics();
    window.addEventListener('resize', this.onResize);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.pendingActiveTimeout = setTimeout(() => {
      this.pendingActiveTimeout = null;
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.cart-drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.cart-drawer__panel') ||
          this.querySelector('.cart-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.cancelPendingOpen();
    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    window.removeEventListener('resize', this.onResize);
  }

  closeImmediate() {
    this.cancelPendingOpen();
    this.classList.add('cart-drawer--instant');
    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    window.removeEventListener('resize', this.onResize);
    requestAnimationFrame(() => {
      this.classList.remove('cart-drawer--instant');
    });
  }

  cancelPendingOpen() {
    clearTimeout(this.pendingRenderTimeout);
    clearTimeout(this.pendingActiveTimeout);
    this.pendingRenderTimeout = null;
    this.pendingActiveTimeout = null;
  }

  onResize = () => {
    this.updateDrawerMetrics();
  };

  updateDrawerMetrics() {
    const header = document.querySelector('.header');
    if (!header) return;

    const headerBottom = Math.round(header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty(
      '--cart-drawer-top',
      `${headerBottom}px`
    );
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${headerBottom}px`
    );
    document.documentElement.style.setProperty(
      '--header-height',
      `${Math.round(header.getBoundingClientRect().height)}px`
    );
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', event => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    this.cancelPendingOpen();

    const cartDrawerInner = this.querySelector('.cart-drawer__inner');
    if (cartDrawerInner && cartDrawerInner.classList.contains('is-empty')) {
      cartDrawerInner.classList.remove('is-empty');
    }

    // Also check for cart-items element to match main-cart-items structure
    const cartItems = this.querySelector('cart-items');
    if (cartItems && cartItems.classList.contains('is-empty')) {
      cartItems.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach(section => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) {
        console.warn(
          `Cart drawer section element not found: ${section.id || section.selector}`
        );
        return;
      }

      if (!parsedState.sections || !parsedState.sections[section.id]) {
        console.warn(`Cart drawer section data not found: ${section.id}`);
        return;
      }

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.pendingRenderTimeout = setTimeout(() => {
      this.pendingRenderTimeout = null;
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      if (options.open !== false) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const section = parsed.querySelector(selector);
    if (!section) {
      console.warn(`Section not found with selector: ${selector}`);
      return '';
    }
    return section.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'global-cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'global-cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'global-cart-drawer',
        selector: '.cart-drawer__inner',
      },
      {
        id: 'global-cart-icon-bubble',
        section: 'global-cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
